import React, { useEffect, useState } from "react";
import type { UploadPage } from "./uploadPages";

// A thumbnail for a photo; a label for a PDF or a photo this browser cannot show.
function PageThumb({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!String(file.type || "").toLowerCase().startsWith("image/")) return;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (url) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setUrl(null)}
        className="w-12 h-12 shrink-0 object-cover rounded border border-slate-200 bg-white"
      />
    );
  }
  return (
    <div className="w-12 h-12 shrink-0 grid place-items-center rounded border border-slate-200 bg-white text-xs text-slate-500">
      {String(file.type || "").toLowerCase().includes("pdf") ? "PDF" : "Image"}
    </div>
  );
}

/**
 * The pages of one invoice upload, in the order they will be joined (VI-4).
 * Reorder with Up / Down, drop a page with Remove.
 */
export default function UploadPageList({
  pages,
  disabled,
  onMove,
  onRemove,
}: {
  pages: UploadPage[];
  disabled?: boolean;
  onMove: (index: number, delta: -1 | 1) => void;
  onRemove: (index: number) => void;
}) {
  if (!pages.length) return null;
  const button =
    "px-2 py-1 rounded border border-slate-200 bg-white text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40";
  return (
    <ol className="mt-3 space-y-2" aria-label="Pages in upload order">
      {pages.map((p, i) => (
        <li key={p.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
          <span className="w-6 shrink-0 text-right text-xs font-semibold text-slate-600">{i + 1}.</span>
          <PageThumb file={p.file} />
          <span className="flex-1 min-w-0 truncate text-xs text-slate-700" title={p.file.name}>
            {p.file.name} ({Math.max(1, Math.round(p.file.size / 1024))} KB)
          </span>
          <button
            type="button"
            className={button}
            disabled={disabled || i === 0}
            onClick={() => onMove(i, -1)}
            aria-label={`Move ${p.file.name} up`}
          >
            Up
          </button>
          <button
            type="button"
            className={button}
            disabled={disabled || i === pages.length - 1}
            onClick={() => onMove(i, 1)}
            aria-label={`Move ${p.file.name} down`}
          >
            Down
          </button>
          <button
            type="button"
            className={button}
            disabled={disabled}
            onClick={() => onRemove(i)}
            aria-label={`Remove ${p.file.name}`}
          >
            Remove
          </button>
        </li>
      ))}
    </ol>
  );
}
